import { logger } from './logger';
import { coverWithBitmap, setBitmapVisible } from './overlay';
export const RENDER_TIMEOUT_MS = 2000;
const DIMS_TOLERANCE_PX = 1;
export const sameDims = (a, b) => Math.abs(a.width - b.width) <= DIMS_TOLERANCE_PX &&
    Math.abs(a.height - b.height) <= DIMS_TOLERANCE_PX;
export const nearDims = (a, b, tolerancePx) => Math.abs(a.width - b.width) <= tolerancePx &&
    Math.abs(a.height - b.height) <= tolerancePx;
export class SnapshotStore {
    adapter;
    slot1 = null;
    slot2 = null;
    inFlight = false;
    constructor(adapter) {
        this.adapter = adapter;
    }
    canvas1() {
        return this.slot1;
    }
    canvas2() {
        return this.slot2;
    }
    clear() {
        this.slot1 = null;
        this.slot2 = null;
    }
    promote() {
        this.slot1 = this.slot2;
        this.slot2 = null;
    }
    captureInitial(initial) {
        return this.exclusive(async () => {
            this.slot1 = await this.captureCurrent(initial);
            return this.slot1;
        });
    }
    captureFinal(initial, final) {
        if (this.slot2 && sameDims(this.slot2.dims, final)) {
            return Promise.resolve(this.slot2);
        }
        return this.exclusive(async () => {
            if (sameDims(initial, final)) {
                this.slot2 = await this.captureCurrent(final);
                return this.slot2;
            }
            if (!this.slot1 || !sameDims(this.slot1.dims, initial)) {
                this.slot1 = await this.captureCurrent(initial);
            }
            const unmask = this.mask(this.slot1.canvas);
            try {
                const renderedInTime = await this.relayoutTo(final);
                try {
                    this.slot2 = renderedInTime
                        ? await this.captureCurrent(final)
                        : null;
                }
                finally {
                    await this.relayoutTo(initial);
                }
                return this.slot2;
            }
            finally {
                unmask();
            }
        });
    }
    mask(bitmap) {
        return coverWithBitmap(this.adapter.getMount(), this.adapter.getRoot(), bitmap).release;
    }
    async captureCurrent(dims) {
        const { captureBitmap } = this.adapter;
        if (!captureBitmap) {
            throw new Error('SnapshotStore: adapter supplies no captureBitmap — bitmap path unavailable');
        }
        const canvas = captureBitmap();
        if (!canvas) {
            throw new Error('SnapshotStore: host captureBitmap returned no canvas');
        }
        return { canvas: setBitmapVisible(canvas, false), dims: { ...dims } };
    }
    async exclusive(work) {
        if (this.inFlight) {
            throw new Error('SnapshotStore: capture already in flight');
        }
        this.inFlight = true;
        try {
            return await work();
        }
        finally {
            this.inFlight = false;
        }
    }
    async relayoutTo(dims) {
        const rendered = this.adapter.waitForRender();
        this.adapter.resize({ ...dims });
        return withRenderTimeout(rendered, dims);
    }
}
export function withRenderTimeout(rendered, dims) {
    return new Promise(resolve => {
        const timer = setTimeout(() => {
            logger.info(`[snapshot-store] relayout to ${dims.width}x${dims.height} resolved by ` +
                `the ${RENDER_TIMEOUT_MS}ms timeout — render never completed`);
            resolve(false);
        }, RENDER_TIMEOUT_MS);
        void rendered.then(() => {
            clearTimeout(timer);
            resolve(true);
            return null;
        });
    });
}
//# sourceMappingURL=snapshot-store.js.map